import React, { useState } from "react";
import { Phone, Lock, Loader2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login } from "../actions/authActions";
import axiosInstance from "../api/axiosInstance"

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        identifier: "",
        password: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validate = () => {
        let errors = {};
        if (!formData.identifier) errors.identifier = "identifier number is required";
        else if (!/^[0-9]{10}$/.test(formData.identifier))
            errors.identifier = "Please enter a valid 10-digit identifier number";

        if (!formData.password) errors.password = "Password is required";
        else if (formData.password.length < 2)
            errors.password = "Password must be at least 2 characters";

        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors((prev) => ({ ...prev, [name]: "" })); // Clear field-specific error
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const token = await authenticateUser(formData);
            dispatch(login(token)); // Update Redux state
            navigate("/dashboard", { replace: true }); // Redirect to dashboard
        } catch (error) {
            console.error("Login failed", error);
        } finally {
            setLoading(false);
        }
    };

    const authenticateUser = async (credentials) => {
        try {
            const response = await axiosInstance.post("auth/signIn", credentials);
            return response.data.token;
        } catch (error) {
            if (error.response) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    ...error.response.data.errors,
                }));
            } else {
                console.error("Unexpected error:", error.message);
            }
            throw error;
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
                <h2 className="text-center text-3xl font-bold text-gray-900">Welcome Back</h2>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="identifier"
                                    value={formData.identifier}
                                    onChange={handleChange}
                                    className={`block w-full pl-10 pr-3 py-3 border ${
                                        errors.identifier
                                            ? "border-red-500"
                                            : "border-gray-300"
                                    } rounded-md focus:ring-teal-700 focus:border-teal-700 sm:text-sm`}
                                    placeholder="Enter your email or mobile number"
                                />
                            </div>
                            {errors.identifier && (
                                <p className="mt-1 text-sm text-red-500">{errors.identifier}</p>
                            )}
                        </div>

                        <div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`block w-full pl-10 pr-3 py-3 border ${
                                        errors.password
                                            ? "border-red-500"
                                            : "border-gray-300"
                                    } rounded-md focus:ring-teal-700 focus:border-teal-700 sm:text-sm`}
                                    placeholder="Password"
                                />
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                            )}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Login;